import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Building2, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang, Hreflang } from "@/i18n/LanguageContext";
import { ALL_CATS, homePath } from "@/i18n/slugs";
import { ZoneSelector } from "@/components/ZoneSelector";
import { FAMILIES } from "@/i18n/taxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractorPage() {
  const { lang, T } = useLang();
  const P = T.contractor;
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: "",
    vat_number: "",
    phone: "",
    email: "",
    password: "",
    service_areas: [],
    project_types: []
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, value) => {
    setFormData((prev) => {
      const arr = prev[field];
      if (arr.includes(value)) {
        return { ...prev, [field]: arr.filter((v) => v !== value) };
      } else {
        return { ...prev, [field]: [...arr, value] };
      }
    });
  };

  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    digit: /\d/.test(formData.password),
  };
  const isPasswordValid = passwordChecks.length && passwordChecks.uppercase && passwordChecks.digit;

  const isFormValid = () => {
    return (
      formData.company_name.trim() !== "" &&
      formData.vat_number.trim() !== "" &&
      formData.phone.trim() !== "" &&
      formData.email.trim() !== "" &&
      isPasswordValid &&
      formData.service_areas.length > 0 &&
      formData.project_types.length > 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/contractors`, formData);
      if (res.data.requires_verification) {
        setPendingVerification(true);
        toast.success(P.toastCodeSent);
      } else {
        setIsSuccess(true);
      }
    } catch (error) {
      console.error("Error:", error);
      let message = error.response?.data?.detail || P.toastError;
      if (Array.isArray(message)) {
        message = message.map((m) => m.msg || "").join(" ");
      }
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    try {
      await axios.post(`${API}/contractors/verify-email`, { email: formData.email, code: verifyCode }, { withCredentials: true });
      setIsSuccess(true);
      toast.success(P.toastVerified);
    } catch (error) {
      toast.error(error.response?.data?.detail || P.toastWrongCode);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await axios.post(`${API}/contractors/resend-verification`, { email: formData.email });
      toast.success(P.toastNewCode);
    } catch (error) {
      toast.error(error.response?.data?.detail || P.toastResendFail);
    }
  };

  const seoHead = (
    <>
      <Helmet>
        <title>{P.metaTitle}</title>
        <meta name="description" content={P.sub} />
        <link rel="canonical" href={`https://devismaison.be${lang === "nl" ? "/nl/aannemer" : "/entrepreneur"}`} />
        <meta property="og:locale" content={lang === "nl" ? "nl_BE" : "fr_BE"} />
      </Helmet>
      <Hreflang frPath="/entrepreneur" nlPath="/nl/aannemer" />
    </>
  );

  if (pendingVerification && !isSuccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="contractor-page">
        {seoHead}
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
          <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-orange-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-3">
              {P.verifyTitle}
            </h1>
            <p className="text-slate-600 mb-8">
              {P.verifyText1} <strong>{formData.email}</strong>. {P.verifyText2}
            </p>
            <form onSubmit={handleVerify} className="space-y-6" data-testid="contractor-verify-form">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                required
                className="text-center text-2xl tracking-[0.5em]"
                maxLength={6}
                data-testid="contractor-verify-code-input"
              />
              <Button
                type="submit"
                disabled={isVerifying || verifyCode.length !== 6}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6"
                data-testid="contractor-verify-button"
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {P.verifying}
                  </>
                ) : (
                  P.activate
                )}
              </Button>
              <button
                type="button"
                onClick={handleResendCode}
                className="text-sm text-orange-500 hover:underline"
                data-testid="contractor-verify-resend-button"
              >
                {P.resend}
              </button>
            </form>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="contractor-page">
        {seoHead}
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
          <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-3">
              {P.successTitle}
            </h1>
            <p className="text-slate-600 mb-8">
              {P.successText}
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-slate-900 mb-4">{P.howTitle}</h3>
              <ul className="space-y-3 text-slate-600">
                {P.howSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Button
              onClick={() => navigate(homePath(lang))}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold"
            >
              {P.backHome}
            </Button>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="contractor-page">
      {seoHead}
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 hero-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2 mb-6">
                <Building2 className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-slate-700">{P.badge}</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                {P.h1a}{" "}
                <span className="text-orange-500">{P.h1b}</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                {P.sub}
              </p>

              <ul className="space-y-3 mb-8">
                {P.advantages.map((advantage, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600 justify-center lg:justify-start">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {advantage}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 lg:p-8 border border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <h2 className="font-heading text-2xl font-semibold text-slate-900">
                  {P.formTitle}
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6">{P.formSub}</p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contractor-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name" className="text-slate-700 mb-2 block">
                      {P.companyName}
                    </Label>
                    <Input
                      id="company_name"
                      placeholder={P.companyPlaceholder}
                      value={formData.company_name}
                      onChange={(e) => updateField("company_name", e.target.value)}
                      required
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vat_number" className="text-slate-700 mb-2 block">
                      {P.vat}
                    </Label>
                    <Input
                      id="vat_number"
                      placeholder="BE0123456789"
                      value={formData.vat_number}
                      onChange={(e) => updateField("vat_number", e.target.value)}
                      required
                      data-testid="vat-number-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 mb-2 block">
                      {P.email}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="contact@entreprise.be"
                      value={formData.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      required
                      data-testid="contractor-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                      {P.phone}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+32 470 123 456"
                      value={formData.phone}
                      onChange={(e) => updateField("phone", e.target.value)}
                      required
                      data-testid="contractor-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-slate-700 mb-2 block">
                    {P.password}
                  </Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    required
                    minLength={8}
                    data-testid="contractor-password-input"
                  />
                  {formData.password.length > 0 && (
                    <div className="mt-2 space-y-1 text-xs" data-testid="password-strength-hints">
                      <p className={passwordChecks.length ? "text-green-700" : "text-slate-500"}>
                        {passwordChecks.length ? "✓" : "○"} {P.pwLength}
                      </p>
                      <p className={passwordChecks.uppercase ? "text-green-700" : "text-slate-500"}>
                        {passwordChecks.uppercase ? "✓" : "○"} {P.pwUpper}
                      </p>
                      <p className={passwordChecks.digit ? "text-green-700" : "text-slate-500"}>
                        {passwordChecks.digit ? "✓" : "○"} {P.pwDigit}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-slate-700 mb-3 block">
                    {P.areas}
                  </Label>
                  <ZoneSelector value={formData.service_areas} onChange={(areas) => setFormData((prev) => ({ ...prev, service_areas: areas }))} lang={lang} required />
                </div>

                <div>
                  <Label className="text-slate-700 mb-3 block">
                    {P.types}
                  </Label>
                  <div className="space-y-4 max-h-80 overflow-y-auto border border-slate-200 rounded-xl p-4" data-testid="contractor-types-list">
                    {FAMILIES.map((f) => (
                      <div key={f.key}>
                        <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                          {f.icon} {lang === "nl" ? f.nl : f.fr}
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {ALL_CATS.filter((c) => c.family === f.key).map((type) => (
                            <div key={type.key} className="flex items-center gap-2">
                              <Checkbox
                                id={`type-${type.key}`}
                                checked={formData.project_types.includes(type.key)}
                                onCheckedChange={() => toggleArrayItem("project_types", type.key)}
                                data-testid={`type-${type.key}`}
                              />
                              <Label htmlFor={`type-${type.key}`} className="text-sm text-slate-600 cursor-pointer">
                                {type[lang].name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={!isFormValid() || isLoading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-xl text-lg"
                  data-testid="contractor-submit-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {P.submitting}
                    </>
                  ) : (
                    <>
                      {P.submit}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  {P.legal}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
